import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BRAND_LOGO, BRAND_NAME } from "@/lib/brand";
import PasswordInput from "@/components/PasswordInput";
import api from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Pre-fill from the post-checkout flow (?email=&name=&phone=&session_id=).
  // This is the one-click create-account experience referenced from
  // /post-checkout: the visitor already typed their details into the
  // enrolment modal, so we don't make them type them again.
  // If `name` arrived as a single field, split it on whitespace into
  // first/last so the form shows it in the new two-field layout.
  const _prefillName = params.get("name") || "";
  const _splitIdx = _prefillName.indexOf(" ");
  const _prefillFirst = _splitIdx === -1 ? _prefillName : _prefillName.slice(0, _splitIdx);
  const _prefillLast = _splitIdx === -1 ? "" : _prefillName.slice(_splitIdx + 1);
  const [form, setForm] = useState({
    first_name: params.get("first_name") || _prefillFirst,
    last_name: params.get("last_name") || _prefillLast,
    email: params.get("email") || "",
    password: "",
    phone: params.get("phone") || "",
  });
  const [busy, setBusy] = useState(false);
  const sessionId = params.get("session_id");
  const fromCheckout = Boolean(sessionId);

  // Auto-focus the password field when the rest is already filled — the
  // visitor's eye should land on the one thing they still need to do.
  useEffect(() => {
    if (fromCheckout) {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="register-password"]');
        if (el) el.focus();
      }, 100);
    }
  }, [fromCheckout]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Backend accepts first_name/last_name AND will also reconstruct `name`
      // on save. We still send `name` for back-compat with any older clients.
      const fullName = `${form.first_name} ${form.last_name}`.trim();
      const u = await register({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        name: fullName,
        email: form.email,
        password: form.password,
        phone: form.phone,
        role: "client",
      });
      // If they came in from /post-checkout, claim the pending enrolment.
      if (sessionId) {
        try { await api.post(`/payments/claim-pending/${sessionId}`); } catch { /* server auto-claims on register too, this is belt+braces */ }
        toast.success(`Welcome, ${u.first_name || u.name?.split(" ")[0]}! Your booking is in your portal.`);
        navigate("/portal/bookings");
        return;
      }
      toast.success(`Welcome, ${u.first_name || u.name?.split(" ")[0]}! 🐾`);
      navigate("/portal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-16 w-auto" />
        </Link>
        <div className="card-pp p-8">
          {fromCheckout ? (
            <>
              <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2" data-testid="register-heading-checkout">One last step — choose a password</h1>
              <p className="text-stone-600 mb-6">We've pre-filled your details from checkout. Set a password to unlock your portal — your booking will appear straight away.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2">Create your account</h1>
              <p className="text-stone-600 mb-6">It's free to get started. No card required.</p>
            </>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">First name</label>
                <input
                  data-testid="register-first-name"
                  type="text"
                  required
                  value={form.first_name}
                  onChange={set("first_name")}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">Last name</label>
                <input
                  data-testid="register-last-name"
                  type="text"
                  required
                  value={form.last_name}
                  onChange={set("last_name")}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30"
                />
              </div>
            </div>
            {[
              { k: "email", l: "Email", t: "email" },
              { k: "phone", l: "Phone (optional)", t: "tel" },
            ].map(({ k, l, t }) => (
              <div key={k}>
                <label className="block text-sm font-semibold text-stone-700 mb-1">{l}</label>
                <input
                  data-testid={`register-${k}`}
                  type={t}
                  required={k !== "phone"}
                  value={form[k]}
                  onChange={set(k)}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#33C095]/30"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1">Password</label>
              <PasswordInput
                data-testid="register-password" required
                value={form.password} onChange={set("password")}
              />
            </div>
            <button data-testid="register-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
              {busy ? "Creating account..." : (fromCheckout ? "Create account & see my booking" : "Create Account")}
            </button>
          </form>
          <p className="mt-6 text-sm text-center text-stone-600">
            Already have one?{" "}
            <Link to={sessionId ? `/login?session_id=${encodeURIComponent(sessionId)}&email=${encodeURIComponent(form.email)}` : "/login"} className="text-[#33C095] font-semibold hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
